import React from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Checkbox,
  CloseButton,
  Group,
  NumberInput,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconRefresh, IconTrash } from '@tabler/icons-react';
import type { CalendarExceptionRecord } from '../../../api/schedulingApis';
import { PermissionRestrictedTooltip } from '../../../permissions/scheduler';

interface ExceptionDraft {
  exceptionDate: string;
  isWorking: boolean;
  workingMinutes: number;
  description: string;
}

interface CalendarExceptionsTabProps {
  calendarSelected: boolean;
  tableViewportHeight: string;
  exceptionSearch: string;
  setExceptionSearch: React.Dispatch<React.SetStateAction<string>>;
  filteredExceptions: CalendarExceptionRecord[];
  exceptionDraft: ExceptionDraft;
  setExceptionDraft: React.Dispatch<React.SetStateAction<ExceptionDraft>>;
  parseDateYmd: (value: string) => Date | null;
  formatDateYmd: (value: Date | string | null) => string;
  onAddException: () => Promise<void>;
  onSyncException: (row: CalendarExceptionRecord) => Promise<void>;
  onDeleteException: (id: string) => Promise<void>;
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

export default function CalendarExceptionsTab(props: CalendarExceptionsTabProps) {
  const {
    calendarSelected,
    tableViewportHeight,
    exceptionSearch,
    setExceptionSearch,
    filteredExceptions,
    exceptionDraft,
    setExceptionDraft,
    parseDateYmd,
    formatDateYmd,
    onAddException,
    onSyncException,
    onDeleteException,
    canCreate = true,
    canEdit = true,
    canDelete = true,
  } = props;

  return (
    <Card withBorder radius="md" p="md" style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack style={{ flex: 1, minHeight: 0 }}>
        <Group align="stretch" wrap="nowrap" gap="md">
          <Box style={{ flex: '1 1 64%', minWidth: 0 }}>
            <Group justify="space-between" align="center" h="100%" wrap="nowrap">
              <Title order={4}>Planned exceptions</Title>
              <TextInput
                placeholder="Search by date or description..."
                value={exceptionSearch}
                onChange={(e) => setExceptionSearch(e.currentTarget.value)}
                size="xs"
                rightSection={
                  exceptionSearch ? (
                    <CloseButton aria-label="Clear search" onClick={() => setExceptionSearch('')} />
                  ) : null
                }
                w={{ base: '100%', sm: 260 }}
              />
            </Group>
          </Box>
          <Box style={{ flex: '0 1 32%', minWidth: 280 }}>
            <Group justify="flex-start" align="center" h="100%">
              <Text size="sm" fw={600}>Add new exception</Text>
            </Group>
          </Box>
        </Group>
        <Group align="stretch" wrap="nowrap" gap="md" style={{ flex: 1, minHeight: 0 }}>
          <Box style={{ flex: '1 1 64%', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
              <ScrollArea h={tableViewportHeight} type="auto" offsetScrollbars>
                <Table striped highlightOnHover withTableBorder>
                  <Table.Thead
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      background: 'var(--mantine-color-body)',
                    }}
                  >
                    <Table.Tr>
                      <Table.Th>Date</Table.Th><Table.Th>Working</Table.Th><Table.Th>Minutes</Table.Th><Table.Th>Description</Table.Th><Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredExceptions.length > 0 ? (
                      filteredExceptions.map((ex) => (
                        <Table.Tr key={ex.id}>
                          <Table.Td>{ex.exceptionDate}</Table.Td>
                          <Table.Td>{ex.isWorking ? 'Yes' : 'No'}</Table.Td>
                          <Table.Td>{ex.workingMinutes}</Table.Td>
                          <Table.Td>{ex.description || '-'}</Table.Td>
                          <Table.Td>
                            <Group gap={6} justify="flex-end">
                              <PermissionRestrictedTooltip restricted={!canEdit}>
                                <ActionIcon
                                  variant="light"
                                  disabled={!canEdit}
                                  onClick={() => { void onSyncException(ex); }}
                                >
                                  <IconRefresh size={14} />
                                </ActionIcon>
                              </PermissionRestrictedTooltip>
                              <PermissionRestrictedTooltip restricted={!canDelete}>
                                <ActionIcon
                                  color="red"
                                  variant="light"
                                  disabled={!canDelete}
                                  onClick={() => { void onDeleteException(ex.id); }}
                                >
                                  <IconTrash size={14} />
                                </ActionIcon>
                              </PermissionRestrictedTooltip>
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={5}>
                          <Text size="sm" c="dimmed" ta="center" py="lg" fw={600}>
                            {calendarSelected ? 'No exceptions to show' : 'Calendar not selected'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Stack>
          </Box>
          <Card withBorder radius="md" p="sm" style={{ flex: '0 1 32%', minWidth: 280, height: '100%', minHeight: 0, overflow: 'auto' }}>
            <Stack>
              <Checkbox
                label="Working day"
                checked={exceptionDraft.isWorking}
                onChange={(e) => setExceptionDraft((s) => ({ ...s, isWorking: e.currentTarget.checked }))}
              />
              <DateInput
                label="Date"
                value={parseDateYmd(exceptionDraft.exceptionDate)}
                onChange={(v) => setExceptionDraft((s) => ({ ...s, exceptionDate: formatDateYmd(v) }))}
              />
              <NumberInput
                label="Working minutes"
                value={exceptionDraft.workingMinutes}
                onChange={(v) => setExceptionDraft((s) => ({ ...s, workingMinutes: Number(v || 0) }))}
                min={0}
              />
              <TextInput
                label="Description"
                value={exceptionDraft.description}
                onChange={(e) => setExceptionDraft((s) => ({ ...s, description: e.currentTarget.value }))}
              />
              <PermissionRestrictedTooltip restricted={!canCreate} fullWidth>
                <Button
                  disabled={!calendarSelected || !exceptionDraft.exceptionDate || !canCreate}
                  onClick={() => { void onAddException(); }}
                  fullWidth
                >
                  Add exception
                </Button>
              </PermissionRestrictedTooltip>
            </Stack>
          </Card>
        </Group>
      </Stack>
    </Card>
  );
}
